import React, { useState, useEffect, useCallback, useRef } from 'react';
import { labsAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea } from 'recharts';
import { Upload, FileText, TrendingUp, Brain, Plus, Trash2, Edit2, Check, X, Search, Loader2, AlertTriangle, Download, ChevronDown, ChevronRight, FlaskConical } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FLAG_COLORS = {
  high: 'text-orange-600 bg-orange-50',
  low: 'text-blue-600 bg-blue-50',
  critical: 'text-red-700 bg-red-50 font-bold',
  normal: 'text-green-700 bg-green-50',
};

const CATEGORY_ORDER = ['CBC', 'Renal / Electrolytes', 'Liver Function', 'Glucose / Diabetes', 'Lipids', 'Thyroid', 'Coagulation', 'Cardiac Markers', 'Inflammatory Markers', 'Iron Studies', 'Vitamins', 'Other'];

const TIME_FILTERS = [
  { label: 'All', value: 'all' },
  { label: '30d', value: '30' },
  { label: '3mo', value: '90' },
  { label: '6mo', value: '180' },
  { label: '1yr', value: '365' },
];

export default function PatientLabsTab({ patientId, patient }) {
  const [activeView, setActiveView] = useState('table');
  const [results, setResults] = useState([]);
  const [categories, setCategories] = useState({});
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [selectedTrend, setSelectedTrend] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [editingResult, setEditingResult] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const fileInputRef = useRef(null);

  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCollectionDate, setUploadCollectionDate] = useState('');
  const [uploadReportDate, setUploadReportDate] = useState('');
  const [uploadNote, setUploadNote] = useState('');

  // Manual entry state
  const [manualForm, setManualForm] = useState({
    test_name: '', result_value: '', unit: '', reference_range: '', flag: 'normal', collection_date: '', category: '', notes: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [resultsRes, catsRes, docsRes, summaryRes] = await Promise.all([
        labsAPI.getResults(patientId),
        labsAPI.getCategories(patientId),
        labsAPI.getDocuments(patientId),
        labsAPI.getLatestSummary(patientId),
      ]);
      setResults(resultsRes.data);
      setCategories(catsRes.data);
      setDocuments(docsRes.data);
      if (summaryRes.data?.summary) setAiSummary(summaryRes.data);

      // Auto-expand first category
      const cats = catsRes.data;
      if (Object.keys(cats).length > 0) {
        setExpandedCategories({ [Object.keys(cats)[0]]: true });
      }
    } catch (err) {
      console.error('Error fetching lab data:', err);
    }
    setLoading(false);
  }, [patientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // --- Upload & Extract ---
  const handleUpload = async () => {
    if (!uploadFile) { toast.error('Please select a file'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('patient_id', patientId);
      formData.append('collection_date', uploadCollectionDate);
      formData.append('report_date', uploadReportDate);
      formData.append('note', uploadNote);
      const res = await labsAPI.uploadDocument(formData);
      const docId = res.data.id;
      toast.success('Document uploaded! Extracting lab values...');
      setUploading(false);
      setExtracting(true);

      // AI extraction
      const extractRes = await labsAPI.extractResults(docId);
      const extracted = extractRes.data;
      if (extracted.error) {
        toast.error(extracted.error);
        setExtracting(false);
        setUploadOpen(false);
        fetchData();
        return;
      }

      setReviewData({
        lab_document_id: docId,
        results: extracted.results || [],
        collection_date: extracted.collection_date || uploadCollectionDate,
        report_date: extracted.report_date || uploadReportDate,
        note: uploadNote,
      });
      setExtracting(false);
      setUploadOpen(false);
      setReviewOpen(true);
      // Reset upload form
      setUploadFile(null);
      setUploadCollectionDate('');
      setUploadReportDate('');
      setUploadNote('');
    } catch (err) {
      toast.error('Upload failed: ' + (err.response?.data?.detail || err.message));
      setUploading(false);
      setExtracting(false);
    }
  };

  // --- Save reviewed results ---
  const handleSaveReviewedResults = async () => {
    if (!reviewData || reviewData.results.length === 0) { toast.error('No results to save'); return; }
    setSaving(true);
    try {
      await labsAPI.saveResults({
        patient_id: patientId,
        lab_document_id: reviewData.lab_document_id,
        results: reviewData.results,
        collection_date: reviewData.collection_date,
        report_date: reviewData.report_date,
        note: reviewData.note,
      });
      toast.success(`${reviewData.results.length} lab results saved!`);
      setReviewOpen(false);
      setReviewData(null);
      fetchData();
    } catch (err) {
      toast.error('Save failed: ' + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  // --- Manual entry ---
  const handleManualSave = async () => {
    if (!manualForm.test_name || !manualForm.result_value) { toast.error('Test name and value are required'); return; }
    try {
      await labsAPI.manualEntry({ ...manualForm, patient_id: patientId });
      toast.success('Lab result added!');
      setManualEntryOpen(false);
      setManualForm({ test_name: '', result_value: '', unit: '', reference_range: '', flag: 'normal', collection_date: '', category: '', notes: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed to save: ' + (err.response?.data?.detail || err.message));
    }
  };

  // --- AI Summary ---
  const handleGenerateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const res = await labsAPI.generateSummary(patientId);
      setAiSummary(res.data);
      toast.success('AI summary generated!');
    } catch (err) {
      toast.error('Summary generation failed');
    }
    setGeneratingSummary(false);
  };

  // --- Load trend ---
  const loadTrend = async (testName) => {
    setSelectedTrend(testName);
    try {
      const res = await labsAPI.getTrends(patientId, testName);
      setTrendData(res.data);
    } catch { setTrendData([]); }
  };

  // --- Delete result ---
  const handleDeleteResult = async (id) => {
    try {
      await labsAPI.deleteResult(id);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  // --- Update result inline ---
  const handleSaveEdit = async () => {
    if (!editingResult) return;
    try {
      await labsAPI.updateResult(editingResult.id, editingResult);
      toast.success('Updated');
      setEditingResult(null);
      fetchData();
    } catch { toast.error('Update failed'); }
  };

  // --- Filter results ---
  const filteredResults = results.filter(r => {
    if (searchTerm && !r.normalized_test_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !r.test_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterCategory !== 'all' && r.category !== filterCategory) return false;
    if (timeFilter !== 'all' && r.collection_date) {
      const days = parseInt(timeFilter);
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      const parts = r.collection_date.split('-');
      if (parts.length === 3) {
        const rd = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        if (rd < cutoff) return false;
      }
    }
    return true;
  });

  // --- Group by category ---
  const groupedResults = {};
  filteredResults.forEach(r => {
    const cat = r.category || 'Other';
    if (!groupedResults[cat]) groupedResults[cat] = [];
    groupedResults[cat].push(r);
  });

  const toggleCategory = (cat) => setExpandedCategories(p => ({ ...p, [cat]: !p[cat] }));

  // Most abnormal recent results
  const abnormalResults = results.filter(r => r.flag && r.flag !== 'normal').slice(0, 8);

  // Unique test names for trend selector
  const uniqueTests = [...new Set(results.map(r => r.normalized_test_name))].sort();

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /><span className="ml-2 text-slate-500">Loading lab data...</span></div>;
  }

  return (
    <div className="space-y-4" data-testid="patient-labs-tab">
      {/* Top Actions */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2">
          <Button onClick={() => setUploadOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="upload-lab-btn">
            <Upload className="h-4 w-4 mr-1" /> Upload Lab
          </Button>
          <Button variant="outline" onClick={() => setManualEntryOpen(true)} className="rounded-xl" data-testid="manual-entry-btn">
            <Plus className="h-4 w-4 mr-1" /> Manual Entry
          </Button>
          <Button variant="outline" onClick={handleGenerateSummary} disabled={generatingSummary || results.length === 0} className="rounded-xl" data-testid="ai-summary-btn">
            {generatingSummary ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Brain className="h-4 w-4 mr-1" />}
            AI Summary
          </Button>
        </div>
        <div className="text-sm text-slate-500">{results.length} results from {documents.length} documents</div>
      </div>

      {/* Abnormal Alerts */}
      {abnormalResults.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="abnormal-alerts">
          <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm mb-2">
            <AlertTriangle className="h-4 w-4" /> Abnormal Results
          </div>
          <div className="flex flex-wrap gap-2">
            {abnormalResults.map(r => (
              <span key={r.id} className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${FLAG_COLORS[r.flag] || ''}`}>
                {r.normalized_test_name}: {r.result_value} {r.unit}
                <span className="opacity-60">({r.flag})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* AI Summary */}
      {aiSummary?.summary && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4" data-testid="ai-summary-panel">
          <div className="flex items-center gap-2 text-indigo-700 font-semibold text-sm mb-2">
            <Brain className="h-4 w-4" /> AI Clinical Lab Summary
          </div>
          <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{aiSummary.summary}</div>
          <p className="mt-3 text-xs text-slate-400 italic">
            AI-generated summary for clinician review only. Verify against source documents and clinical context.
            {aiSummary.generated_at && ` Generated: ${formatDate(aiSummary.generated_at)}`}
          </p>
        </div>
      )}

      {/* View Tabs */}
      <Tabs value={activeView} onValueChange={setActiveView}>
        <TabsList className="bg-slate-100 rounded-xl">
          <TabsTrigger value="table" className="rounded-lg data-[state=active]:bg-white"><FileText className="h-3.5 w-3.5 mr-1" />Table</TabsTrigger>
          <TabsTrigger value="trends" className="rounded-lg data-[state=active]:bg-white"><TrendingUp className="h-3.5 w-3.5 mr-1" />Trends</TabsTrigger>
          <TabsTrigger value="documents" className="rounded-lg data-[state=active]:bg-white"><FlaskConical className="h-3.5 w-3.5 mr-1" />Documents</TabsTrigger>
        </TabsList>

        {/* ===== TABLE VIEW ===== */}
        <TabsContent value="table" className="mt-3">
          <div className="flex flex-wrap gap-2 mb-3">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search tests..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 rounded-xl" data-testid="lab-search" />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px] rounded-xl"><SelectValue placeholder="All categories" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORY_ORDER.filter(c => categories[c]).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-1 bg-slate-100 rounded-xl p-0.5">
              {TIME_FILTERS.map(tf => (
                <button key={tf.value} onClick={() => setTimeFilter(tf.value)}
                  className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${timeFilter === tf.value ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-700'}`}>
                  {tf.label}
                </button>
              ))}
            </div>
          </div>

          {filteredResults.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FlaskConical className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="font-medium">No lab results yet</p>
              <p className="text-sm">Upload a lab document or add results manually</p>
            </div>
          ) : (
            <div className="space-y-2">
              {CATEGORY_ORDER.filter(cat => groupedResults[cat]).map(cat => (
                <div key={cat} className="border border-slate-200 rounded-xl overflow-hidden">
                  <button onClick={() => toggleCategory(cat)} className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left" data-testid={`lab-category-${cat}`}>
                    <span className="font-semibold text-sm text-slate-700">{cat} <span className="text-slate-400 font-normal">({groupedResults[cat].length})</span></span>
                    {expandedCategories[cat] ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                  </button>
                  {expandedCategories[cat] && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b border-slate-100 text-xs text-slate-500 uppercase">
                          <th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Test</th><th className="px-4 py-2 text-right">Value</th><th className="px-4 py-2 text-left">Unit</th><th className="px-4 py-2 text-left">Ref Range</th><th className="px-4 py-2 text-center">Flag</th><th className="px-4 py-2 text-center w-20">Actions</th>
                        </tr></thead>
                        <tbody>
                          {groupedResults[cat].map(r => (
                            editingResult?.id === r.id ? (
                              <tr key={r.id} className="border-b border-slate-50 bg-yellow-50">
                                <td className="px-4 py-1.5"><Input type="date" value={editingResult.collection_date || ''} onChange={e => setEditingResult({ ...editingResult, collection_date: e.target.value })} className="h-7 text-xs" /></td>
                                <td className="px-4 py-1.5"><Input value={editingResult.test_name} onChange={e => setEditingResult({ ...editingResult, test_name: e.target.value })} className="h-7 text-xs" /></td>
                                <td className="px-4 py-1.5"><Input value={editingResult.result_value} onChange={e => setEditingResult({ ...editingResult, result_value: e.target.value })} className="h-7 text-xs text-right" /></td>
                                <td className="px-4 py-1.5"><Input value={editingResult.unit || ''} onChange={e => setEditingResult({ ...editingResult, unit: e.target.value })} className="h-7 text-xs" /></td>
                                <td className="px-4 py-1.5"><Input value={editingResult.reference_range || ''} onChange={e => setEditingResult({ ...editingResult, reference_range: e.target.value })} className="h-7 text-xs" /></td>
                                <td className="px-4 py-1.5">
                                  <Select value={editingResult.flag || 'normal'} onValueChange={v => setEditingResult({ ...editingResult, flag: v })}>
                                    <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-white"><SelectItem value="normal">Normal</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="low">Low</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent>
                                  </Select>
                                </td>
                                <td className="px-4 py-1.5 text-center">
                                  <button onClick={handleSaveEdit} className="text-green-600 hover:text-green-800 mr-1"><Check className="h-4 w-4" /></button>
                                  <button onClick={() => setEditingResult(null)} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
                                </td>
                              </tr>
                            ) : (
                              <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                                <td className="px-4 py-2 text-slate-600">{formatDate(r.collection_date) || '-'}</td>
                                <td className="px-4 py-2 font-medium text-slate-800">{r.normalized_test_name || r.test_name}</td>
                                <td className={`px-4 py-2 text-right font-mono font-semibold ${r.flag === 'high' || r.flag === 'critical' ? 'text-red-600' : r.flag === 'low' ? 'text-blue-600' : 'text-slate-800'}`}>{r.result_value}</td>
                                <td className="px-4 py-2 text-slate-500">{r.unit}</td>
                                <td className="px-4 py-2 text-slate-400 text-xs">{r.reference_range}</td>
                                <td className="px-4 py-2 text-center">{r.flag && r.flag !== 'normal' && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${FLAG_COLORS[r.flag]}`}>{r.flag}</span>}</td>
                                <td className="px-4 py-2 text-center">
                                  <button onClick={() => setEditingResult({ ...r })} className="text-slate-400 hover:text-slate-600 mr-1" data-testid={`edit-lab-${r.id}`}><Edit2 className="h-3.5 w-3.5" /></button>
                                  <button onClick={() => handleDeleteResult(r.id)} className="text-slate-400 hover:text-red-500" data-testid={`delete-lab-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                                </td>
                              </tr>
                            )
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
              {/* Other categories not in CATEGORY_ORDER */}
              {Object.keys(groupedResults).filter(c => !CATEGORY_ORDER.includes(c)).map(cat => (
                <div key={cat} className="border border-slate-200 rounded-xl overflow-hidden">
                  <button onClick={() => toggleCategory(cat)} className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
                    <span className="font-semibold text-sm text-slate-700">{cat} <span className="text-slate-400 font-normal">({groupedResults[cat].length})</span></span>
                    {expandedCategories[cat] ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                  </button>
                  {expandedCategories[cat] && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b border-slate-100 text-xs text-slate-500 uppercase">
                          <th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Test</th><th className="px-4 py-2 text-right">Value</th><th className="px-4 py-2 text-left">Unit</th><th className="px-4 py-2 text-left">Ref Range</th><th className="px-4 py-2 text-center">Flag</th><th className="px-4 py-2 text-center w-20">Actions</th>
                        </tr></thead>
                        <tbody>
                          {groupedResults[cat].map(r => (
                            <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                              <td className="px-4 py-2 text-slate-600">{formatDate(r.collection_date) || '-'}</td>
                              <td className="px-4 py-2 font-medium text-slate-800">{r.normalized_test_name || r.test_name}</td>
                              <td className={`px-4 py-2 text-right font-mono font-semibold ${r.flag === 'high' || r.flag === 'critical' ? 'text-red-600' : r.flag === 'low' ? 'text-blue-600' : 'text-slate-800'}`}>{r.result_value}</td>
                              <td className="px-4 py-2 text-slate-500">{r.unit}</td>
                              <td className="px-4 py-2 text-slate-400 text-xs">{r.reference_range}</td>
                              <td className="px-4 py-2 text-center">{r.flag && r.flag !== 'normal' && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${FLAG_COLORS[r.flag]}`}>{r.flag}</span>}</td>
                              <td className="px-4 py-2 text-center">
                                <button onClick={() => setEditingResult({ ...r })} className="text-slate-400 hover:text-slate-600 mr-1"><Edit2 className="h-3.5 w-3.5" /></button>
                                <button onClick={() => handleDeleteResult(r.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ===== TRENDS VIEW ===== */}
        <TabsContent value="trends" className="mt-3">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Test selector */}
            <div className="lg:col-span-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">Select Test</Label>
              <div className="space-y-1 max-h-[400px] overflow-y-auto border border-slate-200 rounded-xl p-2">
                {uniqueTests.length === 0 ? <p className="text-sm text-slate-400 p-2">No data</p> : uniqueTests.map(t => (
                  <button key={t} onClick={() => loadTrend(t)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selectedTrend === t ? 'bg-emerald-100 text-emerald-800 font-medium' : 'hover:bg-slate-50 text-slate-600'}`}
                    data-testid={`trend-select-${t}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            {/* Chart */}
            <div className="lg:col-span-3">
              {!selectedTrend ? (
                <div className="flex items-center justify-center h-[350px] text-slate-400 text-sm">
                  <TrendingUp className="h-8 w-8 mr-2 opacity-40" /> Select a test to view trends
                </div>
              ) : (
                <TrendChart testName={selectedTrend} data={trendData} timeFilter={timeFilter} setTimeFilter={setTimeFilter} />
              )}
            </div>
          </div>
        </TabsContent>

        {/* ===== DOCUMENTS VIEW ===== */}
        <TabsContent value="documents" className="mt-3">
          {documents.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="font-medium">No lab documents uploaded</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {documents.map(doc => (
                <div key={doc.id} className="border border-slate-200 rounded-xl p-4 hover:border-emerald-300 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg"><FileText className="h-5 w-5 text-slate-500" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{doc.original_filename}</p>
                      <p className="text-xs text-slate-400 mt-0.5">Uploaded {formatDate(doc.upload_date)}</p>
                      {doc.collection_date && <p className="text-xs text-slate-500 mt-0.5">Collection: {formatDate(doc.collection_date)}</p>}
                      {doc.note && <p className="text-xs text-emerald-600 mt-1">{doc.note}</p>}
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <a href={`${BACKEND_URL}/api/files/${doc.id}?token=${localStorage.getItem('emr_auth_token')}`}
                       target="_blank" rel="noopener noreferrer"
                       className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1">
                      <Download className="h-3 w-3" /> Download
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ===== UPLOAD DIALOG ===== */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="upload-lab-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-emerald-600" /> Upload Lab Document</DialogTitle><DialogDescription>Upload a lab result document (PDF, JPG, PNG) for AI extraction.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">File (PDF, JPG, PNG) *</Label>
              <Input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => setUploadFile(e.target.files[0])} className="mt-1" data-testid="lab-file-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Collection Date</Label>
                <Input type="date" value={uploadCollectionDate} onChange={e => setUploadCollectionDate(e.target.value)} className="mt-1" data-testid="lab-collection-date" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Report Date</Label>
                <Input type="date" value={uploadReportDate} onChange={e => setUploadReportDate(e.target.value)} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Note</Label>
              <Input placeholder="e.g., Annual physical labs, ER visit" value={uploadNote} onChange={e => setUploadNote(e.target.value)} className="mt-1" data-testid="lab-upload-note" />
            </div>
            <Button onClick={handleUpload} disabled={uploading || extracting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="upload-submit-btn">
              {uploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading...</> : extracting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Extracting with AI...</> : <><Upload className="h-4 w-4 mr-1" /> Upload & Extract</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== AI EXTRACTION REVIEW DIALOG ===== */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-white" data-testid="review-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Brain className="h-5 w-5 text-indigo-600" /> Review Extracted Lab Results</DialogTitle><DialogDescription>Review, edit, or remove extracted values before saving.</DialogDescription></DialogHeader>
          {reviewData && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">{reviewData.results.length} results extracted. Review, edit, or remove before saving.</p>
              {reviewData.collection_date && <p className="text-xs text-slate-400">Collection date: {reviewData.collection_date}</p>}
              <div className="overflow-x-auto border rounded-xl">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 text-xs text-slate-500 uppercase">
                    <th className="px-3 py-2 text-left">Test Name</th><th className="px-3 py-2 text-right">Value</th><th className="px-3 py-2 text-left">Unit</th><th className="px-3 py-2 text-left">Ref Range</th><th className="px-3 py-2 text-center">Flag</th><th className="px-3 py-2 text-left">Category</th><th className="px-3 py-2 w-10"></th>
                  </tr></thead>
                  <tbody>
                    {reviewData.results.map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-1.5"><Input value={r.test_name} onChange={e => { const upd = [...reviewData.results]; upd[i] = { ...upd[i], test_name: e.target.value }; setReviewData({ ...reviewData, results: upd }); }} className="h-7 text-xs" /></td>
                        <td className="px-3 py-1.5"><Input value={r.result_value || ''} onChange={e => { const upd = [...reviewData.results]; upd[i] = { ...upd[i], result_value: e.target.value }; setReviewData({ ...reviewData, results: upd }); }} className="h-7 text-xs text-right" /></td>
                        <td className="px-3 py-1.5"><Input value={r.unit || ''} onChange={e => { const upd = [...reviewData.results]; upd[i] = { ...upd[i], unit: e.target.value }; setReviewData({ ...reviewData, results: upd }); }} className="h-7 text-xs w-20" /></td>
                        <td className="px-3 py-1.5"><Input value={r.reference_range || ''} onChange={e => { const upd = [...reviewData.results]; upd[i] = { ...upd[i], reference_range: e.target.value }; setReviewData({ ...reviewData, results: upd }); }} className="h-7 text-xs w-28" /></td>
                        <td className="px-3 py-1.5 text-center">
                          {r.flag && r.flag !== 'normal' && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${FLAG_COLORS[r.flag] || ''}`}>{r.flag}</span>}
                          {(!r.flag || r.flag === 'normal') && <span className="text-xs text-slate-400">normal</span>}
                        </td>
                        <td className="px-3 py-1.5 text-xs text-slate-400">{r.category || 'Other'}</td>
                        <td className="px-3 py-1.5"><button onClick={() => { const upd = reviewData.results.filter((_, idx) => idx !== i); setReviewData({ ...reviewData, results: upd }); }} className="text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setReviewOpen(false)} className="rounded-xl">Cancel</Button>
                <Button onClick={handleSaveReviewedResults} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="save-reviewed-btn">
                  {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving...</> : <><Check className="h-4 w-4 mr-1" /> Save {reviewData.results.length} Results</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== MANUAL ENTRY DIALOG ===== */}
      <Dialog open={manualEntryOpen} onOpenChange={setManualEntryOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="manual-entry-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5 text-emerald-600" /> Manual Lab Entry</DialogTitle><DialogDescription>Enter a lab result manually.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Test Name *</Label>
                <Input placeholder="e.g., Hemoglobin" value={manualForm.test_name} onChange={e => setManualForm({ ...manualForm, test_name: e.target.value })} className="mt-1" data-testid="manual-test-name" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Value *</Label>
                <Input placeholder="e.g., 12.4" value={manualForm.result_value} onChange={e => setManualForm({ ...manualForm, result_value: e.target.value })} className="mt-1" data-testid="manual-result-value" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Unit</Label>
                <Input placeholder="e.g., g/dL" value={manualForm.unit} onChange={e => setManualForm({ ...manualForm, unit: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reference Range</Label>
                <Input placeholder="e.g., 12.0-16.0" value={manualForm.reference_range} onChange={e => setManualForm({ ...manualForm, reference_range: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Flag</Label>
                <Select value={manualForm.flag} onValueChange={v => setManualForm({ ...manualForm, flag: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="normal">Normal</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="low">Low</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Collection Date</Label>
                <Input type="date" value={manualForm.collection_date} onChange={e => setManualForm({ ...manualForm, collection_date: e.target.value })} className="mt-1" data-testid="manual-collection-date" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notes</Label>
                <Input placeholder="Optional note" value={manualForm.notes} onChange={e => setManualForm({ ...manualForm, notes: e.target.value })} className="mt-1" />
              </div>
            </div>
            <Button onClick={handleManualSave} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="manual-save-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Result
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- TREND CHART COMPONENT ---
function TrendChart({ testName, data, timeFilter, setTimeFilter }) {
  const filteredData = data.filter(d => {
    if (timeFilter === 'all') return true;
    if (!d.collection_date) return false;
    const days = parseInt(timeFilter);
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    const parts = d.collection_date.split('-');
    if (parts.length === 3) {
      const rd = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      return rd >= cutoff;
    }
    return true;
  }).map(d => ({
    ...d,
    numericValue: parseFloat(d.result_value) || 0,
    displayDate: d.collection_date || '',
  }));

  // Parse reference range
  let refLow = null, refHigh = null;
  if (data.length > 0 && data[0].reference_range) {
    const match = data[0].reference_range.match(/([\d.]+)\s*[-–]\s*([\d.]+)/);
    if (match) { refLow = parseFloat(match[1]); refHigh = parseFloat(match[2]); }
  }

  const unit = data.length > 0 ? data[0].unit : '';

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-800">{testName} {unit && <span className="text-sm text-slate-400 font-normal">({unit})</span>}</h3>
        <div className="flex gap-1 bg-slate-100 rounded-xl p-0.5">
          {TIME_FILTERS.map(tf => (
            <button key={tf.value} onClick={() => setTimeFilter(tf.value)}
              className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${timeFilter === tf.value ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-700'}`}>
              {tf.label}
            </button>
          ))}
        </div>
      </div>
      {filteredData.length === 0 ? (
        <div className="flex items-center justify-center h-[300px] text-slate-400 text-sm">No data for selected time range</div>
      ) : (
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={filteredData} margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="displayDate" tick={{ fontSize: 11, fill: '#64748b' }} angle={-30} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} domain={['auto', 'auto']} />
            <Tooltip
              contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '13px' }}
              formatter={(value, name) => [`${value} ${unit}`, testName]}
              labelFormatter={l => `Date: ${l}`}
            />
            {refLow !== null && refHigh !== null && (
              <ReferenceArea y1={refLow} y2={refHigh} fill="#d1fae5" fillOpacity={0.4} label={{ value: 'Normal Range', position: 'insideTopRight', fontSize: 10, fill: '#059669' }} />
            )}
            {refLow !== null && <ReferenceLine y={refLow} stroke="#059669" strokeDasharray="4 4" />}
            {refHigh !== null && <ReferenceLine y={refHigh} stroke="#059669" strokeDasharray="4 4" />}
            <Line type="monotone" dataKey="numericValue" stroke="#059669" strokeWidth={2.5} dot={(props) => {
              const { cx, cy, payload } = props;
              const color = payload.flag === 'high' || payload.flag === 'critical' ? '#dc2626' : payload.flag === 'low' ? '#2563eb' : '#059669';
              return <circle cx={cx} cy={cy} r={5} fill={color} stroke="white" strokeWidth={2} />;
            }} activeDot={{ r: 7 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
      {/* Data table below chart */}
      {filteredData.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-slate-400 border-b"><th className="px-3 py-1.5 text-left">Date</th><th className="px-3 py-1.5 text-right">Value</th><th className="px-3 py-1.5 text-center">Flag</th><th className="px-3 py-1.5 text-left">Change</th></tr></thead>
            <tbody>
              {filteredData.map((d, i) => {
                const prev = i > 0 ? filteredData[i - 1].numericValue : null;
                const change = prev !== null ? d.numericValue - prev : null;
                return (
                  <tr key={d.id || i} className="border-b border-slate-50">
                    <td className="px-3 py-1.5 text-slate-600">{formatDate(d.displayDate)}</td>
                    <td className={`px-3 py-1.5 text-right font-mono font-semibold ${d.flag === 'high' || d.flag === 'critical' ? 'text-red-600' : d.flag === 'low' ? 'text-blue-600' : 'text-slate-800'}`}>{d.result_value} {unit}</td>
                    <td className="px-3 py-1.5 text-center">{d.flag && d.flag !== 'normal' && <span className={`px-1.5 py-0.5 rounded-full text-xs ${FLAG_COLORS[d.flag]}`}>{d.flag}</span>}</td>
                    <td className="px-3 py-1.5">{change !== null && <span className={change > 0 ? 'text-red-500' : change < 0 ? 'text-blue-500' : 'text-slate-400'}>{change > 0 ? '+' : ''}{change.toFixed(1)}</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
